import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Stack,
  Autocomplete,
  Chip,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { Item, Category, CreateItemRequest, UpdateItemRequest, InventoryMode, ItemType, PriceEntryMode } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';
import { getInventoryMode } from '../../utils/inventoryMode';
import { DEFAULT_VAT_RATE, isSupportedVatRate, VAT_RATE_OPTIONS } from '../../constants/vat';
import { AppDialogFormActions } from '../common/AppDialog';

interface ItemFormProps {
  item?: Item | null;
  onSubmit: (data: CreateItemRequest | UpdateItemRequest) => Promise<void>;
  onCancel: () => void;
  priceEntryMode?: PriceEntryMode;
  defaultVatRate?: number;
  batchTrackingEnabled?: boolean;
  isLoading?: boolean;
  formId?: string;
  showActions?: boolean;
}

const ItemForm: React.FC<ItemFormProps> = ({
  item,
  onSubmit,
  onCancel,
  priceEntryMode,
  defaultVatRate = DEFAULT_VAT_RATE,
  batchTrackingEnabled,
  isLoading = false,
  formId,
  showActions = true,
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    itemNumber: '',
    barcode: '',
    price: '',
    vatRate: String(defaultVatRate),
    itemType: ItemType.PRODUCT,
    initialStockQuantity: '0',
    enableBatchTracking: false,
    initialBatchNumber: '',
    initialProductionDate: '',
    initialExpirationDate: '',
    initialBatchNotes: '',
  });
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditing = Boolean(item);
  const canUseBatchTracking = batchTrackingEnabled !== false;
  const effectivePriceEntryMode = item?.priceEntryMode || priceEntryMode || PriceEntryMode.VAT_EXCLUDED;

  const enteredPriceFromItem = useCallback((currentItem: Item): string => {
    if (effectivePriceEntryMode === PriceEntryMode.VAT_INCLUDED) {
      if (currentItem.priceGross != null) return currentItem.priceGross.toString();
      if (currentItem.price == null) return '';
      return (Math.round(currentItem.price * (1 + (currentItem.vatRate || 0) / 100) * 100) / 100).toString();
    }
    return currentItem.price?.toString() || currentItem.priceGross?.toString() || '';
  }, [effectivePriceEntryMode]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await apiClient.getCategories();
        setAvailableCategories(categories);
      } catch (error: unknown) {
        setSubmitError(errorMessage(error, 'Failed to load categories'));
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        itemNumber: item.itemNumber || '',
        barcode: item.barcode || '',
        price: enteredPriceFromItem(item),
        vatRate: item.vatRate?.toString() || '0',
        itemType: item.itemType || ItemType.PRODUCT,
        initialStockQuantity: item.totalStockQuantity?.toString() || '0',
        enableBatchTracking: canUseBatchTracking && getInventoryMode(item) === InventoryMode.BATCHED,
        initialBatchNumber: '',
        initialProductionDate: '',
        initialExpirationDate: '',
        initialBatchNotes: '',
      });
      setSelectedCategories(item.categories || []);
    } else {
      setFormData({
        name: '',
        itemNumber: '',
        barcode: '',
        price: '',
        vatRate: String(defaultVatRate),
        itemType: ItemType.PRODUCT,
        initialStockQuantity: '0',
        enableBatchTracking: false,
        initialBatchNumber: '',
        initialProductionDate: '',
        initialExpirationDate: '',
        initialBatchNotes: '',
      });
      setSelectedCategories([]);
    }
    setErrors({});
    setSubmitError(null);
  }, [batchTrackingEnabled, canUseBatchTracking, defaultVatRate, enteredPriceFromItem, item, priceEntryMode]);

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'enableBatchTracking' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('items.nameRequired');
    }

    const codePattern = /^[A-Za-z0-9][A-Za-z0-9._/-]*$/;
    if (formData.itemNumber.trim() && !codePattern.test(formData.itemNumber.trim())) {
      newErrors.itemNumber = t('items.invalidCode');
    }
    if (formData.barcode.trim() && !codePattern.test(formData.barcode.trim())) {
      newErrors.barcode = t('items.invalidCode');
    }
    if (formData.initialBatchNumber.trim() && !codePattern.test(formData.initialBatchNumber.trim())) {
      newErrors.initialBatchNumber = t('items.invalidCode');
    }
    if (
      !isEditing
      && formData.itemType === ItemType.PRODUCT
      && formData.enableBatchTracking
      && Number(formData.initialStockQuantity) > 0
      && !formData.initialBatchNumber.trim()
    ) {
      newErrors.initialBatchNumber = t('inventory.batchNumberRequired');
    }

    if (formData.itemType === ItemType.PRODUCT && !formData.price.trim()) {
      newErrors.price = t('items.priceRequired');
    } else if (formData.price.trim() && isNaN(Number(formData.price))) {
      newErrors.price = t('items.priceRequired');
    } else if (formData.price.trim() && Number(formData.price) <= 0) {
      newErrors.price = t('items.pricePositive');
    }

    const vatRate = Number(formData.vatRate);
    if (
      formData.vatRate.trim() === ''
      || !Number.isFinite(vatRate)
      || vatRate < 0
      || !isSupportedVatRate(vatRate)
    ) {
      newErrors.vatRate = t('items.vatRateInvalid');
    }

    if (!isEditing && formData.itemType === ItemType.PRODUCT
      && (!Number.isInteger(Number(formData.initialStockQuantity)) || Number(formData.initialStockQuantity) < 0 || formData.initialStockQuantity.trim() === '')) {
      newErrors.initialStockQuantity = t('inventory.invalidQuantity');
    }
    if (!isEditing && formData.itemType === ItemType.PRODUCT && formData.enableBatchTracking && formData.initialProductionDate && formData.initialExpirationDate && formData.initialExpirationDate < formData.initialProductionDate) {
      newErrors.initialExpirationDate = t('inventory.expirationAfterProduction');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: CreateItemRequest | UpdateItemRequest = {
        name: formData.name.trim(),
        categoryIds: selectedCategories.map(cat => cat.id),
        vatRate: Number(formData.vatRate),
      };
      if (formData.price.trim()) {
        submitData.price = Number(formData.price);
      } else if (isEditing && formData.itemType === ItemType.SERVICE) {
        // An explicit null clears an existing optional service price. Omitting
        // the field would make the backend preserve the old catalog price.
        (submitData as UpdateItemRequest).price = null;
      }

      if (!isEditing) {
        (submitData as CreateItemRequest).itemType = formData.itemType;
        if (formData.itemNumber.trim()) {
          (submitData as CreateItemRequest).itemNumber = formData.itemNumber.trim();
        }
        if (formData.barcode.trim()) {
          (submitData as CreateItemRequest).barcode = formData.barcode.trim();
        }
      } else {
        if (formData.itemNumber.trim() !== (item?.itemNumber || '')) {
          (submitData as UpdateItemRequest).itemNumber = formData.itemNumber.trim();
        }
        if (formData.barcode.trim() !== (item?.barcode || '')) {
          (submitData as UpdateItemRequest).barcode = formData.barcode.trim();
        }
      }

      // Add inventory fields for create requests
      if (!isEditing) {
        (submitData as CreateItemRequest).initialStockQuantity = formData.itemType === ItemType.PRODUCT
          ? Number(formData.initialStockQuantity)
          : 0;
        (submitData as CreateItemRequest).enableBatchTracking = formData.itemType === ItemType.PRODUCT
          ? canUseBatchTracking && formData.enableBatchTracking
          : false;
        (submitData as CreateItemRequest).inventoryMode = formData.itemType === ItemType.SERVICE
          ? InventoryMode.NONE
          : canUseBatchTracking && formData.enableBatchTracking ? InventoryMode.BATCHED : InventoryMode.SIMPLE;
        if (formData.itemType === ItemType.PRODUCT && canUseBatchTracking && formData.enableBatchTracking) {
          if (formData.initialBatchNumber.trim()) {
            (submitData as CreateItemRequest).initialBatchNumber = formData.initialBatchNumber.trim();
          }
          if (formData.initialProductionDate) {
            (submitData as CreateItemRequest).initialProductionDate = formData.initialProductionDate;
          }
          if (formData.initialExpirationDate) {
            (submitData as CreateItemRequest).initialExpirationDate = formData.initialExpirationDate;
          }
          if (formData.initialBatchNotes.trim()) {
            (submitData as CreateItemRequest).initialBatchNotes = formData.initialBatchNotes.trim();
          }
        }
      } else if (canUseBatchTracking
        && formData.enableBatchTracking !== Boolean(item?.enableBatchTracking)) {
        // Batch tracking is a separate state transition. Do not resend the
        // unchanged value while editing another item field. When the
        // capability is unavailable, omit the transition entirely: the
        // caller may be a read-only cross-company administrator and the
        // hidden toggle must never downgrade a tracked item to SIMPLE.
        (submitData as UpdateItemRequest).enableBatchTracking = formData.enableBatchTracking;
        (submitData as UpdateItemRequest).inventoryMode = formData.enableBatchTracking
          ? InventoryMode.BATCHED
          : formData.itemType === ItemType.SERVICE ? InventoryMode.NONE : InventoryMode.SIMPLE;
      }

      await onSubmit(submitData);
    } catch (error: unknown) {
      setSubmitError(errorMessage(error, 'Failed to save item. Please try again.'));
    }
  };

  return (
    <Box component="form" id={formId} onSubmit={handleSubmit} sx={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={700}>{t('items.management')}</Typography>
        {/* Item Name */}
        <TextField
          fullWidth
          inputProps={{ 'data-testid': 'item-name' }}
          label={t('items.itemName')}
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          disabled={isLoading}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <InventoryIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Item code */}
        <TextField
          fullWidth
          inputProps={{ 'data-testid': 'item-number', maxLength: 50 }}
          label={t('items.itemNumber')}
          value={formData.itemNumber}
          onChange={handleChange('itemNumber')}
          error={!!errors.itemNumber}
          helperText={errors.itemNumber || t('items.itemNumberHelp')}
          disabled={isLoading}
        />

        {/* Barcode */}
        <TextField
          fullWidth
          inputProps={{ 'data-testid': 'item-barcode', maxLength: 50 }}
          label={t('items.barcode')}
          value={formData.barcode}
          onChange={handleChange('barcode')}
          error={!!errors.barcode}
          helperText={errors.barcode || t('items.barcodeHelp')}
          disabled={isLoading}
        />

        {/* Price */}
        <FormControl fullWidth disabled={isLoading || isEditing}>
          <InputLabel>{t('items.itemType')}</InputLabel>
          <Select
            value={formData.itemType}
            label={t('items.itemType')}
            onChange={event => setFormData(previous => ({ ...previous, itemType: event.target.value as ItemType }))}
            inputProps={{ 'data-testid': 'item-type' }}
          >
            <MenuItem value={ItemType.PRODUCT}>{t('items.productType')}</MenuItem>
            <MenuItem value={ItemType.SERVICE}>{t('items.serviceType')}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label={formData.itemType === ItemType.SERVICE ? t('items.defaultPriceOptional') : t('items.salePrice')}
          value={formData.price}
          onChange={handleChange('price')}
          error={!!errors.price}
          helperText={errors.price || `${formData.itemType === ItemType.SERVICE ? t('items.servicePriceHelp') : t('items.salePricePerUnitRequired')} ${t('items.priceEntryModeHint').replace('{mode}', effectivePriceEntryMode === PriceEntryMode.VAT_INCLUDED ? t('items.priceEntryModeVatIncluded') : t('items.priceEntryModeVatExcluded'))}`}
          disabled={isLoading}
          required={formData.itemType === ItemType.PRODUCT}
          type="number"
          inputProps={{
            'data-testid': 'item-price',
            min: 0,
            step: 0.01,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                TRY
              </InputAdornment>
            ),
          }}
        />

        <FormControl fullWidth error={!!errors.vatRate} disabled={isLoading}>
          <InputLabel id="item-vat-rate-label">{t('items.vatRate')}</InputLabel>
          <Select
            labelId="item-vat-rate-label"
            value={formData.vatRate}
            label={t('items.vatRate')}
            inputProps={{ 'data-testid': 'item-vat-rate' }}
            onChange={(event) => setFormData(prev => ({ ...prev, vatRate: event.target.value }))}
          >
            {VAT_RATE_OPTIONS.map(rate => (
              <MenuItem key={rate} value={String(rate)}>%{rate}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.vatRate || t('items.vatRateHelp')}</FormHelperText>
        </FormControl>

        {/* Categories */}
        <Autocomplete
          multiple
          options={availableCategories}
          getOptionLabel={(option) => option.name}
          value={selectedCategories}
          onChange={(_, newValue) => {
            setSelectedCategories(newValue);
          }}
          disabled={isLoading}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name}
                {...getTagProps({ index })}
                key={option.id}
                sx={{ borderColor: option.color || 'divider', bgcolor: option.color ? `${option.color}18` : undefined }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('categories.categories')}
              placeholder={t('categories.selectCategories')}
              variant="outlined"
              fullWidth
            />
          )}
        />

        {formData.itemType === ItemType.PRODUCT && <>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, mt: 1 }}><Typography variant="subtitle1" fontWeight={700}>{t('inventory.currentStock')}</Typography><Typography variant="body2" color="text.secondary">{canUseBatchTracking ? t('inventory.batchTrackingHelp') : t('inventory.simpleInventoryHelp')}</Typography></Box>

        {/* Initial Stock Quantity - only for new items */}
        {!isEditing && (
          <TextField
            fullWidth
            label={t('inventory.initialStock')}
            value={formData.initialStockQuantity}
            onChange={handleChange('initialStockQuantity')}
            error={!!errors.initialStockQuantity}
            helperText={errors.initialStockQuantity || t('inventory.initialStockHelp')}
            disabled={isLoading}
            type="number"
            inputProps={{
              'data-testid': 'item-initial-stock',
              min: 0,
              step: 1,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t('inventory.units')}
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Batch Tracking */}
        {canUseBatchTracking && <>
        <FormControlLabel
          control={
            <Switch
              checked={formData.enableBatchTracking}
              onChange={handleChange('enableBatchTracking')}
              disabled={isLoading}
            />
          }
          label={
            <Box>
              <Typography variant="body2">{t('inventory.enableBatchTracking')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('inventory.batchTrackingHelp')}
              </Typography>
            </Box>
          }
        />

        {/* Initial batch details */}
        {!isEditing && formData.enableBatchTracking && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">{t('inventory.batchDetails')}</Typography>
            <TextField
              fullWidth
              label={t('inventory.batchNumber')}
              value={formData.initialBatchNumber}
              onChange={handleChange('initialBatchNumber')}
              error={!!errors.initialBatchNumber}
              inputProps={{ maxLength: 100 }}
              disabled={isLoading}
              helperText={errors.initialBatchNumber || t('inventory.batchNumberPlaceholder')}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="date"
                label={t('inventory.productionDate')}
                value={formData.initialProductionDate}
                onChange={handleChange('initialProductionDate')}
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label={t('inventory.expirationDate')}
                value={formData.initialExpirationDate}
                onChange={handleChange('initialExpirationDate')}
                disabled={isLoading}
                error={!!errors.initialExpirationDate}
                helperText={errors.initialExpirationDate}
                inputProps={{ min: formData.initialProductionDate || undefined }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              fullWidth
              multiline
              label={t('inventory.notes')}
              value={formData.initialBatchNotes}
              onChange={handleChange('initialBatchNotes')}
              inputProps={{ maxLength: 500 }}
              disabled={isLoading}
            />
          </Stack>
        )}
        </>}
        </>}
      </Stack>

      {/* Actions */}
      {showActions && <AppDialogFormActions>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          color="inherit"
        >
          {t('common.cancel')}
        </Button>
        <Button
          data-testid="item-submit"
          type="submit"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('items.saving') : isEditing ? t('items.update') : t('items.create')}
        </Button>
      </AppDialogFormActions>}
    </Box>
  );
};

export default ItemForm;
